/**
 * Work Progress Manager Component
 * Enhanced progress tracking with status updates and team coordination
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  Grid,
  Chip,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Avatar,
  Badge,
  Tooltip,
  Stack,
  Alert,
  Divider,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stepper,
  Step,
  StepLabel,
  StepContent
} from '@mui/material';
import {
  PlayArrow as StartIcon,
  Pause as PauseIcon,
  Stop as StopIcon,
  CheckCircle as CompleteIcon,
  Assignment as TaskIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon,
  Comment as CommentIcon,
  Refresh as RefreshIcon,
  Timeline as TimelineIcon,
  ExpandMore as ExpandIcon,
  Edit as EditIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { formatDistanceToNow, format } from 'date-fns';

import plannedWorksService from '../../services/plannedWorksService';
import {
  PlannedWorkDetail,
  WorkTodoItem,
  WorkProgressUpdate,
  WorkStatus
} from '../../types/plannedWorks';

interface WorkProgressManagerProps {
  work: PlannedWorkDetail;
  onWorkUpdated: (work: PlannedWorkDetail) => void;
  readOnly?: boolean;
}

interface StatusUpdateData {
  status: WorkStatus;
  notes: string;
  completion_percentage?: number;
}

interface TodoUpdateData {
  id: number;
  status: 'pending' | 'in_progress' | 'completed' | 'blocked' | 'skipped';
  notes?: string;
  actual_duration_minutes?: number;
}

const STATUS_COLORS = {
  draft: 'default',
  proposed: 'info',
  approved: 'success',
  scheduled: 'warning',
  in_progress: 'primary',
  on_hold: 'warning',
  completed: 'success',
  cancelled: 'error'
} as const;

const STATUS_ACTIONS = {
  draft: ['propose'],
  proposed: ['approve', 'reject'],
  approved: ['schedule'],
  scheduled: ['start', 'cancel'],
  in_progress: ['pause', 'complete'],
  on_hold: ['resume', 'cancel'],
  completed: [],
  cancelled: []
} as const;

export const WorkProgressManager: React.FC<WorkProgressManagerProps> = ({
  work,
  onWorkUpdated,
  readOnly = false
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [commentDialogOpen, setCommentDialogOpen] = useState(false);
  const [progressHistory, setProgressHistory] = useState<WorkProgressUpdate[]>([]);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    progress: true,
    todos: true,
    timeline: false
  });
  
  // Form states
  const [statusUpdate, setStatusUpdate] = useState<StatusUpdateData>({
    status: work.status,
    notes: '',
    completion_percentage: work.completion_percentage
  });
  
  const [todoUpdates, setTodoUpdates] = useState<Record<number, TodoUpdateData>>({});
  const [newComment, setNewComment] = useState('');

  useEffect(() => {
    fetchProgressHistory();
  }, [work.id]);

  const fetchProgressHistory = async () => {
    try {
      const history = await plannedWorksService.plannedWorks.getProgressHistory(work.id);
      setProgressHistory(history);
    } catch (err) {
      console.error('Failed to fetch progress history:', err);
    }
  };

  const calculateProgress = () => {
    if (!work.todo_items || work.todo_items.length === 0) {
      return work.completion_percentage || 0;
    }
    
    const completed = work.todo_items.filter(todo => todo.status === 'completed').length;
    return Math.round((completed / work.todo_items.length) * 100);
  };

  const getTodoStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'success';
      case 'in_progress': return 'primary';
      case 'blocked': return 'error';
      case 'skipped': return 'warning';
      default: return 'default';
    }
  };

  const handleStatusAction = async (action: string) => {
    try {
      setLoading(true);
      setError(null);
      
      let updatedWork: PlannedWorkDetail;
      
      switch (action) {
        case 'propose':
          updatedWork = await plannedWorksService.plannedWorks.propose(work.id, {
            notes: statusUpdate.notes
          });
          break;
        case 'approve':
          updatedWork = await plannedWorksService.plannedWorks.approve(work.id, {
            notes: statusUpdate.notes
          });
          break;
        case 'reject':
          updatedWork = await plannedWorksService.plannedWorks.reject(work.id, {
            notes: statusUpdate.notes
          });
          break;
        case 'schedule':
          updatedWork = await plannedWorksService.plannedWorks.schedule(work.id, {
            notes: statusUpdate.notes
          });
          break;
        case 'start':
          updatedWork = await plannedWorksService.plannedWorks.startWork(work.id, {
            notes: statusUpdate.notes
          });
          break;
        case 'pause':
          updatedWork = await plannedWorksService.plannedWorks.pauseWork(work.id, {
            notes: statusUpdate.notes
          });
          break;
        case 'resume':
          updatedWork = await plannedWorksService.plannedWorks.resumeWork(work.id, {
            notes: statusUpdate.notes
          });
          break;
        case 'complete':
          updatedWork = await plannedWorksService.plannedWorks.completeWork(work.id, {
            notes: statusUpdate.notes,
            completion_percentage: 100
          });
          break;
        case 'cancel':
          updatedWork = await plannedWorksService.plannedWorks.cancelWork(work.id, {
            notes: statusUpdate.notes
          });
          break;
        default:
          throw new Error(`Unknown action: ${action}`);
      }
      
      onWorkUpdated(updatedWork);
      setStatusDialogOpen(false);
      setStatusUpdate({ status: updatedWork.status, notes: '', completion_percentage: updatedWork.completion_percentage });
      fetchProgressHistory();
    } catch (err) {
      console.error(`Failed to ${action} work:`, err);
      setError(`Failed to ${action} work: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleTodoUpdate = async (todoId: number, update: Partial<TodoUpdateData>) => {
    try {
      setLoading(true);
      
      const updatedTodo = await plannedWorksService.workTodoItems.update(todoId, {
        status: update.status,
        notes: update.notes,
        actual_duration_minutes: update.actual_duration_minutes
      });
      
      // Update local work state
      const updatedWork = {
        ...work,
        todo_items: work.todo_items?.map(todo =>
          todo.id === todoId ? { ...todo, ...updatedTodo } : todo
        )
      };
      
      onWorkUpdated(updatedWork);
    } catch (err) {
      console.error('Failed to update todo:', err);
      setError('Failed to update task');
    } finally {
      setLoading(false);
    }
  };

  const addProgressComment = async () => {
    if (!newComment.trim()) return;
    
    try {
      setLoading(true);
      await plannedWorksService.plannedWorks.addComment(work.id, {
        comment: newComment
      });
      
      setNewComment('');
      setCommentDialogOpen(false);
      fetchProgressHistory();
    } catch (err) {
      console.error('Failed to add comment:', err);
      setError('Failed to add comment');
    } finally {
      setLoading(false);
    }
  };

  const getAvailableActions = () => {
    return STATUS_ACTIONS[work.status] || [];
  };

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {/* Progress Overview */}
      <Accordion 
        expanded={expandedSections.progress} 
        onChange={() => setExpandedSections(prev => ({ ...prev, progress: !prev.progress }))}
      >
        <AccordionSummary expandIcon={<ExpandIcon />}>
          <Box display="flex" alignItems="center" gap={2} width="100%">
            <TimelineIcon color="primary" />
            <Typography variant="h6">Work Progress</Typography>
            <Box ml="auto" mr={2}>
              <Chip
                label={work.status_display}
                color={STATUS_COLORS[work.status]}
                size="small"
              />
            </Box>
          </Box>
        </AccordionSummary>
        
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Box mb={2}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="body2" color="textSecondary">
                    Overall Progress
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {calculateProgress()}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={calculateProgress()}
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>
              
              {work.assigned_to_name && (
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <PersonIcon fontSize="small" color="action" />
                  <Typography variant="body2">
                    Assigned to: {work.assigned_to_name}
                  </Typography>
                </Box>
              )}
              
              {work.actual_start_date && (
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <ScheduleIcon fontSize="small" color="action" />
                  <Typography variant="body2">
                    Started: {format(new Date(work.actual_start_date), 'PPpp')}
                  </Typography>
                </Box>
              )}
            </Grid>
            
            <Grid item xs={12} md={4}>
              {!readOnly && (
                <Stack spacing={1}>
                  {getAvailableActions().map(action => (
                    <Button
                      key={action}
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        setStatusUpdate({ status: work.status, notes: '', completion_percentage: work.completion_percentage });
                        setStatusDialogOpen(true);
                      }}
                      disabled={loading}
                      startIcon={
                        action === 'start' ? <StartIcon /> :
                        action === 'pause' ? <PauseIcon /> :
                        action === 'complete' ? <CompleteIcon /> :
                        <EditIcon />
                      }
                    >
                      {action.charAt(0).toUpperCase() + action.slice(1)}
                    </Button>
                  ))}
                  
                  <Button
                    variant="text"
                    size="small"
                    startIcon={<CommentIcon />}
                    onClick={() => setCommentDialogOpen(true)}
                  >
                    Add Comment
                  </Button>
                </Stack>
              )}
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
      
      {/* Task Progress */}
      {work.todo_items && work.todo_items.length > 0 && (
        <Accordion 
          expanded={expandedSections.todos} 
          onChange={() => setExpandedSections(prev => ({ ...prev, todos: !prev.todos }))}
        >
          <AccordionSummary expandIcon={<ExpandIcon />}>
            <Box display="flex" alignItems="center" gap={2}>
              <TaskIcon color="primary" />
              <Typography variant="h6">
                Tasks ({work.todo_items.filter(t => t.status === 'completed').length}/{work.todo_items.length})
              </Typography>
            </Box>
          </AccordionSummary>
          
          <AccordionDetails>
            <List>
              {work.todo_items.map((todo, index) => (
                <React.Fragment key={todo.id}>
                  <ListItem>
                    <ListItemIcon>
                      <Chip
                        size="small"
                        label={index + 1}
                        color={getTodoStatusColor(todo.status)}
                        variant={todo.status === 'completed' ? 'filled' : 'outlined'}
                      />
                    </ListItemIcon>
                    
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="body1">{todo.title}</Typography>
                          {todo.requires_safety_check && (
                            <Chip size="small" label="Safety Check" color="warning" />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="textSecondary">
                            {todo.description}
                          </Typography>
                          <Box display="flex" gap={1} mt={0.5}>
                            <Typography variant="caption">
                              Est: {formatDuration(todo.estimated_duration_minutes)}
                            </Typography>
                            {todo.actual_duration_minutes && (
                              <Typography variant="caption">
                                Actual: {formatDuration(todo.actual_duration_minutes)}
                              </Typography>
                            )}
                            {todo.assigned_to_name && (
                              <Typography variant="caption">
                                Assigned: {todo.assigned_to_name}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      }
                    />
                    
                    {!readOnly && todo.status !== 'completed' && (
                      <ListItemSecondaryAction>
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                          <Select
                            value={todo.status}
                            onChange={(e) => handleTodoUpdate(todo.id, { status: e.target.value as any })}
                            disabled={loading}
                          >
                            <MenuItem value="pending">Pending</MenuItem>
                            <MenuItem value="in_progress">In Progress</MenuItem>
                            <MenuItem value="completed">Complete</MenuItem>
                            <MenuItem value="blocked">Blocked</MenuItem>
                            <MenuItem value="skipped">Skip</MenuItem>
                          </Select>
                        </FormControl>
                      </ListItemSecondaryAction>
                    )}
                  </ListItem>
                  
                  {index < work.todo_items.length - 1 && <Divider variant="inset" component="li" />}
                </React.Fragment>
              ))}
            </List>
          </AccordionDetails>
        </Accordion>
      )}
      
      {/* Progress Timeline */}
      <Accordion 
        expanded={expandedSections.timeline} 
        onChange={() => setExpandedSections(prev => ({ ...prev, timeline: !prev.timeline }))}
      >
        <AccordionSummary expandIcon={<ExpandIcon />}>
          <Box display="flex" alignItems="center" gap={2}>
            <TimelineIcon color="primary" />
            <Typography variant="h6">Progress History</Typography>
            <IconButton size="small" onClick={fetchProgressHistory}>
              <RefreshIcon />
            </IconButton>
          </Box>
        </AccordionSummary>
        
        <AccordionDetails>
          {progressHistory.length > 0 ? (
            <Stepper orientation="vertical">
              {progressHistory.map((update, index) => (
                <Step key={index} active={true} completed={true}>
                  <StepLabel>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body2" fontWeight="medium">
                        {update.action}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {formatDistanceToNow(new Date(update.created_at), { addSuffix: true })}
                      </Typography>
                    </Box>
                  </StepLabel>
                  
                  <StepContent>
                    <Box>
                      {update.notes && (
                        <Typography variant="body2" color="textSecondary" mb={1}>
                          {update.notes}
                        </Typography>
                      )}
                      
                      <Box display="flex" alignItems="center" gap={1}>
                        <Avatar sx={{ width: 24, height: 24 }}>
                          {update.created_by.first_name[0]}
                        </Avatar>
                        <Typography variant="caption">
                          {update.created_by.first_name} {update.created_by.last_name}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {format(new Date(update.created_at), 'PPp')}
                        </Typography>
                      </Box>
                    </Box>
                  </StepContent>
                </Step>
              ))}
            </Stepper>
          ) : (
            <Typography variant="body2" color="textSecondary">
              No progress history available
            </Typography>
          )}
        </AccordionDetails>
      </Accordion>
      
      {/* Status Update Dialog */}
      <Dialog open={statusDialogOpen} onClose={() => setStatusDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Update Work Status</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Notes"
            value={statusUpdate.notes}
            onChange={(e) => setStatusUpdate(prev => ({ ...prev, notes: e.target.value }))}
            placeholder="Add notes about this status change..."
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialogOpen(false)}>Cancel</Button>
          {getAvailableActions().map(action => (
            <Button
              key={action}
              variant="contained"
              onClick={() => handleStatusAction(action)}
              disabled={loading}
            >
              {action.charAt(0).toUpperCase() + action.slice(1)}
            </Button>
          ))}
        </DialogActions>
      </Dialog>
      
      {/* Comment Dialog */}
      <Dialog open={commentDialogOpen} onClose={() => setCommentDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Progress Comment</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Comment"
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Add a comment about the work progress..."
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCommentDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={addProgressComment}
            disabled={loading || !newComment.trim()}
          >
            Add Comment
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WorkProgressManager;
